import { Button, FormLayout, TextInput } from 'components/common'
import SwitchField from 'components/bundleProduct/switchField'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { ConfigurationWithId } from 'types/configuration'
import { getInputErrorMessage } from 'utils/helpers/getNestedErrorMessage'
import { handleError } from 'utils/helpers/handleError'
import { kdsConfigFields } from './utils'
import {
  kdsConfigSchema,
  KdsConfigFormFields,
} from 'utils/schema/kdsConfigSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import { triggerKdsManualSync, updateKdsConfig } from 'utils/api/configApi'

const {
  CREATE_ORDER_URL,
  CANCEL_ORDER_URL,
  USERNAME,
  PASSWORD,
  WEBHOOK_SECRET,
  SLA_MINUTES,
  IS_ENABLED,
} = kdsConfigFields

type Props = {
  configuration?: ConfigurationWithId
}

const KdsConfigurationForm = ({ configuration }: Props) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<KdsConfigFormFields>({
    resolver: yupResolver(kdsConfigSchema),
    defaultValues: {
      slaMinutes: 0,
      ...(configuration?.kds || {}),
    },
  })

  const [loading, setLoading] = useState(false)
  const [syncLoading, setSyncLoading] = useState(false)

  const handleSave = async (data: KdsConfigFormFields) => {
    const configToast = toast.loading('Updating KDS config...')
    setLoading(true)
    try {
      await updateKdsConfig(data)
      toast.success('KDS config updated successfully.', {
        id: configToast,
      })
    } catch (error) {
      handleError(error, configToast, {
        default: 'Failed to update KDS config.',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleManualSync = async () => {
    const syncToast = toast.loading('Triggering KDS manual sync...')
    setSyncLoading(true)
    try {
      const { data } = await triggerKdsManualSync()
      toast.success(
        data?.message || 'KDS manual sync triggered successfully.',
        {
          id: syncToast,
        },
      )
    } catch (error) {
      handleError(error, syncToast, {
        default: 'Failed to trigger KDS manual sync.',
      })
    } finally {
      setSyncLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(handleSave)}>
      {/* ================= INTEGRATION ================= */}
      <h3 className="title mt-4">Integration Configuration</h3>

      <FormLayout>
        {/* Top Section: Enable Toggle */}
        <SwitchField
          control={control}
          name={IS_ENABLED.key}
          label={IS_ENABLED.label}
          description="Enable or disable KDS integration"
          showStatusPill
        />

        <div className="mb-5 border-t" />

        <div className="grid gap-4 mid-md:grid-cols-2">
          <TextInput
            required
            register={register}
            name={CREATE_ORDER_URL.key}
            label={CREATE_ORDER_URL.label}
            error={getInputErrorMessage(errors, CREATE_ORDER_URL)}
            placeholder="https://..."
          />

          <TextInput
            required
            register={register}
            name={CANCEL_ORDER_URL.key}
            label={CANCEL_ORDER_URL.label}
            error={getInputErrorMessage(errors, CANCEL_ORDER_URL)}
            placeholder="https://..."
          />

          <TextInput
            required
            register={register}
            name={USERNAME.key}
            label={USERNAME.label}
            error={getInputErrorMessage(errors, USERNAME)}
          />

          <TextInput
            required
            type="password"
            register={register}
            name={PASSWORD.key}
            label={PASSWORD.label}
            error={getInputErrorMessage(errors, PASSWORD)}
          />

          <TextInput
            required
            type="password"
            register={register}
            name={WEBHOOK_SECRET.key}
            label={WEBHOOK_SECRET.label}
            error={getInputErrorMessage(errors, WEBHOOK_SECRET)}
          />
        </div>
      </FormLayout>

      {/* ================= CRON ================= */}
      <h3 className="title mt-4">Cron Configuration</h3>

      <FormLayout>
        <div className="grid gap-4 mid-md:grid-cols-2">
          <TextInput
            required
            type="number"
            min={0}
            step={1}
            register={register}
            name={SLA_MINUTES.key}
            label={SLA_MINUTES.label}
            error={getInputErrorMessage(errors, SLA_MINUTES)}
            hintText="Number of minutes added to the order's planned delivery time (or current time, if delivery time has already passed) to compute the target dispatch time sent to KDS."
            placeholder="e.g., 15"
          />
        </div>
      </FormLayout>

      <div className="mt-5 flex items-center gap-3">
        <Button loading={loading} buttonText="Save" className="w-32" />

        <Button
          type="button"
          variant="outline"
          loading={syncLoading}
          disabled={syncLoading || loading}
          buttonText="Trigger Manual Sync"
          className="w-48"
          onClick={handleManualSync}
        />
      </div>
    </form>
  )
}

export default KdsConfigurationForm
